import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TournamentTypes } from 'common';
import { TournamentStatus } from 'common/dist/infrastructure/modules/game/interfaces/TournamentTypes';
import { FC, useMemo } from 'react';

import { useCaseGetTournamentLeaderboard } from 'application/modules';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import Trophy1stIcon from 'targets/web/assets/icons/trophy-1st.svg?react';
import Trophy2ndIcon from 'targets/web/assets/icons/trophy-2nd.svg?react';
import Trophy3rdIcon from 'targets/web/assets/icons/trophy-3rd.svg?react';
import { formatToCurrency } from 'targets/web/modules/display/utils/tournamentUtils';

// Styled components
const LeaderboardContainer = styled(Stack)({
  width: '100%',
  flex: 1,
});

const LeaderboardHeader = styled(Stack)({
  flexDirection: 'row',
  backgroundColor: '#262626',
  padding: '16px',
});

const LeaderboardRow = styled(Stack)({
  flexDirection: 'row',
  borderBottom: '1px solid #333',
  padding: '0 16px',
  alignItems: 'center',
  height: 98,
});

const ScoreText = styled(Typography)({
  color: '#22C55E',
});

// Mock data for the leaderboard
const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy1stIcon />;
    case 2:
      return <Trophy2ndIcon />;
    case 3:
      return <Trophy3rdIcon />;
    default:
      return null;
  }
};

interface TournamentCardBodyLeaderboardProps {
  tournament: TournamentTypes.TournamentConfig;
}

export const TournamentCardBodyLeaderboard: FC<TournamentCardBodyLeaderboardProps> = ({
  tournament,
}) => {
  const t = useTranslationPrefix('tournament');
  const { isLoading, data: leaderboard } = useCaseGetTournamentLeaderboard(tournament.id);
  const groupedLeaderboardItems = useMemo(() => {
    const groupedByPosition = leaderboard.reduce((acc: Record<number, typeof leaderboard>, player: typeof leaderboard[0]) => {
      const pos = player.total.pos;
      const key = pos != null ? pos : Infinity;
      if (!acc[key]) acc[key] = [];
      acc[key].push(player);
      return acc;
    }, {} as Record<number, typeof leaderboard>);

    return Object.entries(groupedByPosition)
      .sort(([a], [b]) => Number(a) - Number(b))
       
      .flatMap(([_key, group]) =>
        (group as typeof leaderboard).map((player: typeof leaderboard[0], index: number) => ({
          ...player,
          showFull: index === 0 || player.total.pos == null,
          isLastInGroup: index === (group as typeof leaderboard).length - 1,
        })),
      );
  }, [leaderboard]);

  const leaderboardToDisplay = useMemo(
    () => groupedLeaderboardItems?.slice(0, 3) ?? [],
    [groupedLeaderboardItems],
  );

  return (
    <LeaderboardContainer>
      <LeaderboardHeader>
        <Box sx={{ display: 'flex', flex: '0 0 80px', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '24px' }}>Rank</Typography>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '24px' }}>Player</Typography>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '24px' }}>Thru</Typography>
        </Box>
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '24px' }}>Score</Typography>
        </Box>
      </LeaderboardHeader>

      {isLoading ? (
        <Stack
          gap={6}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={40} />
          <Typography sx={{ fontFamily: 'RawsonSemiBold', fontSize: '24px' }}>
            {t('fetchingLeaderboard')}
          </Typography>
        </Stack>
      ) : leaderboardToDisplay.length > 0 ? (
        <Stack>
          {leaderboardToDisplay.map((item) => {
            const thruLabel = item.total.state === 'PLAYED' ? 'F' : item.latestRound?.thru ?? '-';
            const showWinnings = item.showFull || tournament.status === TournamentStatus.ENDED;

            return (
              <LeaderboardRow key={item.id}>
                <Box sx={{ display: 'flex', flex: '0 0 80px', alignItems: 'center' }}>
                  {item.total.pos != null ? getRankIcon(item.total.pos) : null}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                  }}>
                  <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '40px' }}>
                    {item.playername
                      ? `${item.playername.substring(0, 15)}${
                          item.playername.length > 15 ? '...' : ''
                        }`
                      : ''}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '40px' }}>
                    {thruLabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}>
                  <Stack flexDirection="row" gap={2} alignItems="center">
                    {showWinnings && item.possibleWinnings && item.possibleWinnings > 0 && (
                      <>
                        <ScoreText sx={{ fontFamily: 'RawsonBlack', fontSize: '40px' }}>
                          {`${formatToCurrency(item.possibleWinnings)}`}
                        </ScoreText>
                        <Box sx={{ width: '1px', height: '50px', backgroundColor: 'white' }} />
                      </>
                    )}
                    <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '40px' }}>
                      {item.total.toPar != null
                        ? `${item.total.toPar > 0 ? '+' : ''}${
                            item.total.toPar === 0 ? 'E' : item.total.toPar
                          }`
                        : '-'}
                    </Typography>
                  </Stack>
                </Box>
              </LeaderboardRow>
            );
          })}
        </Stack>
      ) : (
        <Stack sx={{ height: '100%' }} justifyContent="center" alignItems="center">
          <Typography sx={{ fontFamily: 'RawsonBlack', fontSize: '32px' }}>
            {t('waitingForPlayers')}
          </Typography>
        </Stack>
      )}
    </LeaderboardContainer>
  );
};
